import { splitAnswerSignature } from '@/lib/answerSignature';
import { parseDataTable } from '@/lib/dataTable';
import { DISPLAYED_ROWS_CAP } from '@/lib/fullTable';
import { openSkill, parseSkillLink } from '@/lib/skills';
import { cn } from '@/lib/utils';
import { omit } from 'lodash';
import { LayoutGrid } from 'lucide-react';
import { useContext, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { PluggableList } from 'react-markdown/lib';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import remarkDirective from 'remark-directive';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import { visit } from 'unist-util-visit';

import { ChainlitContext, type IMessageElement } from '@chainlit/react-client';

import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Separator } from '@/components/ui/separator';

import {
  Callout,
  FreshnessStamp,
  ITEM_HEADER_TITLE,
  ItemHeader,
  Provenance
} from './AnswerBlocks';
import BlinkingCursor from './BlinkingCursor';
import CodeSnippet from './CodeSnippet';
import { DataTable } from './DataTable';
import { ElementRef } from './Elements/ElementRef';
import {
  type AlertProps,
  MarkdownAlert,
  alertComponents,
  normalizeAlertType
} from './MarkdownAlert';

interface Props {
  allowHtml?: boolean;
  latex?: boolean;
  renderMarkdown?: boolean;
  refElements?: IMessageElement[];
  /** The full table behind a table cut to DISPLAYED_ROWS_CAP rows. */
  fullTableFile?: IMessageElement;
  children: string;
  className?: string;
}

const cursorPlugin = () => {
  return (tree: any) => {
    visit(tree, 'text', (node: any, index, parent) => {
      const placeholderPattern = /\u200B/g;
      const matches = [...(node.value?.matchAll(placeholderPattern) || [])];

      if (matches.length > 0) {
        const newNodes: any[] = [];
        let lastIndex = 0;

        matches.forEach((match) => {
          const [fullMatch] = match;
          const startIndex = match.index!;
          const endIndex = startIndex + fullMatch.length;

          if (startIndex > lastIndex) {
            newNodes.push({
              type: 'text',
              value: node.value!.slice(lastIndex, startIndex)
            });
          }

          newNodes.push({
            type: 'blinkingCursor',
            data: {
              hName: 'blinkingCursor',
              hProperties: { text: 'Blinking Cursor' }
            }
          });

          lastIndex = endIndex;
        });

        if (lastIndex < node.value!.length) {
          newNodes.push({
            type: 'text',
            value: node.value!.slice(lastIndex)
          });
        }

        parent!.children.splice(index, 1, ...newNodes);
      }
    });
  };
};

/**
 * `dir="auto"` on anything that renders model or user text.
 *
 * The chat is the surface that renders the most Arabic and was the only one
 * that never declared direction: punctuation landed on the wrong side of the
 * line and tables rendered LTR. Auto resolves per element from its own first
 * strong character, which is what one thread holding both languages needs — the
 * document itself stays LTR.
 */
const AUTO = { dir: 'auto' as const };

const Markdown = ({
  allowHtml,
  latex,
  renderMarkdown,
  refElements,
  fullTableFile,
  className,
  children
}: Props) => {
  const apiClient = useContext(ChainlitContext);

  const rehypePlugins = useMemo(() => {
    let rehypePlugins: PluggableList = [];
    if (allowHtml) {
      rehypePlugins = [rehypeRaw as any, ...rehypePlugins];
    }
    if (latex) {
      rehypePlugins = [rehypeKatex as any, ...rehypePlugins];
    }
    return rehypePlugins;
  }, [allowHtml, latex]);

  const remarkPlugins = useMemo(() => {
    let remarkPlugins: PluggableList = [
      cursorPlugin,
      remarkGfm as any,
      remarkDirective as any,
      MarkdownAlert
    ];

    if (latex) {
      remarkPlugins = [...remarkPlugins, remarkMath as any];
    }
    return remarkPlugins;
  }, [latex]);

  // The method and freshness lines are lifted out of the markdown source before
  // it is parsed; they get their own blocks below the answer.
  const { body, method, freshness } = useMemo(
    () => splitAnswerSignature(children),
    [children]
  );

  // Hooks run first: this early return used to sit above them, which made the
  // hook order depend on a prop.
  if (renderMarkdown === false) {
    return (
      <pre
        className={cn('whitespace-pre-wrap break-words', className)}
        style={{ fontFamily: 'inherit' }}
        dir="auto"
      >
        {children}
      </pre>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full min-w-0">
      <ReactMarkdown
        className={cn('signals-answer text-[14.5px] leading-[1.6]', className)}
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={{
          ...alertComponents, // add alert components
          code(props) {
            return (
              <code
                {...omit(props, ['node'])}
                dir="ltr"
                className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-[12.5px] font-semibold"
              />
            );
          },
          pre({ children, ...props }: any) {
            return <CodeSnippet {...props} />;
          },
          a({ children, ...props }) {
            const name = children as string;
            const element = refElements?.find((e) => e.name === name);
            // An answer that has a matching skill carries a link to open it
            // filled in (signals-copilot server/skill_requests.offer_link).
            const skillLink = parseSkillLink(String(props.href || ''));
            if (skillLink) {
              return (
                <button
                  type="button"
                  onClick={() => openSkill(skillLink.id, skillLink.values)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-[13px] text-primary hover:bg-muted transition-colors"
                >
                  <LayoutGrid className="!size-3.5" />
                  {children}
                </button>
              );
            }
            if (element) {
              return <ElementRef element={element} />;
            } else {
              return (
                <a
                  {...props}
                  className="text-primary hover:underline"
                  target="_blank"
                >
                  {children}
                </a>
              );
            }
          },
          img: (image: any) => {
            // Check if the image source is actually a video file
            const src = image.src.startsWith('/public')
              ? apiClient.buildEndpoint(image.src)
              : image.src;

            if (image.title === ITEM_HEADER_TITLE) {
              return <ItemHeader src={src} label={image.alt} />;
            }

            const videoExtensions = [
              '.mp4',
              '.webm',
              '.mov',
              '.avi',
              '.ogv',
              '.m4v'
            ];
            const isVideo = videoExtensions.some((ext) =>
              src.toLowerCase().split(/[?#]/)[0].endsWith(ext)
            );

            if (isVideo) {
              return (
                <div className="sm:max-w-sm md:max-w-md">
                  <video
                    src={src}
                    controls
                    className="w-full h-auto rounded-md"
                    style={{ maxWidth: '100%' }}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              );
            }

            return (
              <div className="sm:max-w-sm md:max-w-md">
                <AspectRatio
                  ratio={16 / 9}
                  className="bg-muted rounded-md overflow-hidden"
                >
                  <img
                    src={src}
                    alt={image.alt}
                    className="h-full w-full object-contain"
                  />
                </AspectRatio>
              </div>
            );
          },
          // The caveat is usually the sentence that stops a figure being
          // misread. As an italic blockquote it read as decoration.
          blockquote({ children }) {
            return <Callout>{children}</Callout>;
          },
          em(props) {
            return <span {...omit(props, ['node'])} className="italic" />;
          },
          strong(props) {
            return (
              <span {...omit(props, ['node'])} className="font-semibold" />
            );
          },
          hr() {
            return <Separator />;
          },
          ul(props) {
            return (
              <ul
                {...omit(props, ['node'])}
                {...AUTO}
                className="my-3 ms-3 list-disc ps-2 [&>li]:mt-1"
              />
            );
          },
          ol(props) {
            return (
              <ol
                {...omit(props, ['node'])}
                {...AUTO}
                className="my-3 ms-3 list-decimal ps-2 [&>li]:mt-1"
              />
            );
          },
          li(props) {
            return <li {...omit(props, ['node'])} {...AUTO} />;
          },
          // The answer starts with the answer: no 30px article heading and no
          // rule under it.
          h1(props) {
            return (
              <h1
                {...omit(props, ['node'])}
                {...AUTO}
                className="text-[21px] font-bold leading-[1.45] tracking-[-.01em] mt-6 first:mt-0 sig-pretty"
                style={{ color: 'var(--sig-ink)' }}
              />
            );
          },
          h2(props) {
            return (
              <h2
                {...omit(props, ['node'])}
                {...AUTO}
                className="text-[19px] font-semibold leading-[1.5] tracking-[-.01em] mt-5 first:mt-0 sig-pretty"
                style={{ color: 'var(--sig-ink)' }}
              />
            );
          },
          h3(props) {
            return (
              <h3
                {...omit(props, ['node'])}
                {...AUTO}
                className="text-[15.5px] font-semibold leading-[1.5] mt-4 first:mt-0"
                style={{ color: 'var(--sig-ink)' }}
              />
            );
          },
          h4(props) {
            return (
              <h4
                {...omit(props, ['node'])}
                {...AUTO}
                className="text-[14px] font-semibold leading-[1.5] mt-4 first:mt-0"
                style={{ color: 'var(--sig-ink)' }}
              />
            );
          },
          p(props) {
            return (
              <div
                {...omit(props, ['node'])}
                {...AUTO}
                className="text-[14.5px] leading-[1.6] [&:not(:first-child)]:mt-3 whitespace-pre-wrap break-words sig-pretty"
                style={{ color: 'var(--sig-ink)' }}
                role="article"
              />
            );
          },
          // A markdown table in an answer is data, not prose. Reading it from
          // the node gives real values to sort, copy and export; a table the
          // parser cannot make sense of falls through to plain markup rather
          // than disappearing from the answer.
          table({ children, node, ...props }: any) {
            const model = parseDataTable(node);
            if (!model) {
              return (
                <div className="overflow-x-auto">
                  <table {...props} className="w-full text-[13px]">
                    {children}
                  </table>
                </div>
              );
            }
            // Only a table that was cut gets the full file: a small table
            // the model wrote in its text is exported as it stands.
            const cut = model.rows.length >= DISPLAYED_ROWS_CAP;
            return (
              <DataTable
                model={model}
                fullTableFile={cut ? fullTableFile : undefined}
              />
            );
          },
          // @ts-expect-error custom plugin
          blinkingCursor: () => <BlinkingCursor whitespace />,
          alert: ({
            type,
            children,
            ...props
          }: AlertProps & { type?: string }) => {
            const alertType = normalizeAlertType(
              type || props.variant || 'info'
            );
            return alertComponents.Alert({ variant: alertType, children });
          }
        }}
      >
        {body}
      </ReactMarkdown>

      {method ? (
        <Provenance summary="Where this came from">{method}</Provenance>
      ) : null}

      {freshness ? <FreshnessStamp text={freshness} /> : null}
    </div>
  );
};

export { Markdown };
